/* Shared storefront helpers, loaded once from layout/theme.liquid <head> so they
   are defined before any section/snippet inline <script> runs. These replace ~20
   copy-pasted local definitions across the theme.

   Exposed as globals (the theme's inline scripts run non-strict, so a bare `esc(x)`
   resolves here once the local copy is removed) and namespaced under window.TTT.

   Note: files with an intentionally different local helper keep their own version,
   which shadows these globals within that closure (e.g. card-search-engine.js has a
   local quote-stripping `esc` and its own money formatter). */
(function () {
  // HTML-escape for safe interpolation into innerHTML. Superset of the old copies:
  // also escapes ' (always safe), and null/undefined render as '' (not "null").
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // USD money formatter: money(12.5) -> "$12.50". Non-numbers -> "$0.00".
  function money(n) {
    return '$' + (Number(n) || 0).toFixed(2);
  }

  // Unified toast — replaces four divergent copies. Usage:
  //   toast('Deck saved')                 neutral (dark, no icon)
  //   toast('Added to wishlist', {ok:1})   success (dark + gold check)
  //   toast('Added to cart', {cart:1})     success + "View cart →" link
  //   toast('Could not save', {error:1})   error (red)
  // Back-compat for the old call sites: 2nd arg true → {ok}, false → {error},
  // 'error' → {error}, 'cart' → {cart}. Always sits above modals (z-index 10000).
  function toast(msg, opts) {
    var o = opts;
    if (o === true) o = { ok: true };
    else if (o === false) o = { error: true };
    else if (o === 'error') o = { error: true };
    else if (o === 'cart') o = { cart: true };
    else if (!o || typeof o !== 'object') o = {};

    var err = !!o.error;
    var showIcon = !err && (o.ok || o.cart);
    var t = document.createElement('div');
    t.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%) translateY(10px);' +
      'z-index:10000;display:flex;align-items:center;gap:.55rem;background:' + (err ? '#b4452f' : '#1C1712') +
      ';color:#FCFAF5;font-family:var(--font-body,sans-serif);font-size:.92rem;padding:.8rem 1.25rem;' +
      'border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.3);opacity:0;transition:opacity .25s,transform .25s';
    var icon = showIcon ? '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#D8A43C" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>' : '';
    var link = o.cart && !err ? '<a href="/cart" style="color:#D8A43C;font-weight:700;text-decoration:none;margin-left:.35rem;white-space:nowrap">View cart →</a>' : '';
    t.innerHTML = icon + '<span></span>' + link;
    t.querySelector('span').textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.style.opacity = '1'; t.style.transform = 'translateX(-50%) translateY(0)'; });
    setTimeout(function () {
      t.style.opacity = '0'; t.style.transform = 'translateX(-50%) translateY(10px)';
      setTimeout(function () { t.remove(); }, 300);
    }, 3000);
  }

  // Product title → { name, treatment, flavorName }. One parser, because every local copy
  // of this got the catalogs wrong in a different way.
  //
  // Three title shapes are in the wild:
  //   Model A  "Sol Ring (C13 259) [Commander 2013]"   — a SETCODE+number hint
  //   reskin   "Loran of the Third Path (Garnet Til Alexandros 17th) (FCA 24) [Final Fantasy…]"
  //   old      "Sol Ring (Retro) [Love Your LGS 2022]" — no hint; the parens are
  //                                                      the TREATMENT
  // Pass the set NAME if you have it; otherwise any trailing [bracket] is dropped.
  //
  // What a surviving parenthetical MEANS depends on whether the SETCODE hint was there:
  // Model A never puts a treatment in the title (it tags them), so a group left standing after
  // the hint is stripped is the VANITY NAME printed on a reskin — badging it as a treatment
  // would tell a seller to hunt for a "Garnet Til Alexandros 17th" finish that doesn't exist.
  // Old-catalog titles have no hint, so their trailing group is still the treatment.
  var PRINTING_HINT = /\s*\([A-Z0-9]{2,6}\s+[^()]{1,12}\)\s*$/;
  var TRAIL_PAREN = /\s*\(([^()]*)\)\s*$/;
  // The Model-A "(SET NUM)" anchor, matched ANYWHERE — mirrors titleAnchor in lib/treatments.ts.
  // The set token needs a LETTER SOMEWHERE, not first: digit-leading codes (3ED, 2X2, 40K, 4ED,
  // 2XM, 5DN, 10E …) are real set codes on 221 prod products, and requiring a leading letter made
  // every one of them read as an anchor-less legacy title. Note PRINTING_HINT above already used
  // [A-Z0-9]{2,6} and so already accepted them — the two patterns disagreed with each other.
  var TITLE_ANCHOR = /\((?=[A-Z0-9]*[A-Z])[A-Z0-9]{2,6}\s+[^)]*\d[^)]*\)/;

  /* Split a Model-A title's leading "<printed name> - " prefix off the front.
   *
   * Returns [printedName, rest]; ['', title] when there is no prefix. Two guards, both needed,
   * because legacy Storepass titles use " - " for two OTHER things:
   *   1. the title must carry the (SET NUM) anchor — legacy titles have none, and theirs is
   *      either a trailing set separator ("… (MSH-350) - Marvel Super Heroes") or, on Secret
   *      Lairs, ALREADY a flavor separator ("Air Shoes - Swiftfoot Boots");
   *   2. the separator must sit before the anchor and outside any parenthetical (legacy titles
   *      also hide one inside a group, "(Rainbow Foil - Japanese)").
   * Measured: all 3,995 prod titles containing " - " are legacy, so the two formats do not
   * overlap, and no MTG card or flavor name contains " - " at all.
   *
   * That last fact is Magic-only. Riftbound and SWU names CONTAIN " - " ("Ahri - Inquisitive")
   * and carry the same anchor, so the product TYPE is a third guard: anything that is not Magic
   * has no printed-name prefix. Without it "Ahri - Inquisitive" rendered as a separate card named
   * "Inquisitive". An unknown/absent type keeps the Magic reading. Mirrors oracleTitle in
   * lib/treatments.ts. */
  function splitPrinted(title, productType) {
    var t = String(title == null ? '' : title);
    var g = productType ? cardGame(productType) : null;
    if (g && g !== 'MTG') return ['', t];
    var a = t.search(TITLE_ANCHOR);
    if (a < 0) return ['', t];
    var i = t.indexOf(' - ');
    if (i < 0 || i >= a) return ['', t];
    var before = t.slice(0, i);
    if (before.lastIndexOf('(') > before.lastIndexOf(')')) return ['', t];
    return [before.trim(), t.slice(i + 3).replace(/^\s+/, '')];
  }
  function oracleTitle(title, productType) { return splitPrinted(title, productType)[1]; }
  function printedNameFromTitle(title, productType) { return splitPrinted(title, productType)[0]; }

  // Pass the product type whenever it is known — it is what keeps "Ahri - Inquisitive" whole.
  function cardTitle(title, set, productType) {
    var n = String(title == null ? '' : title);
    // Take the printed-name prefix off the FULL title, before any bracket stripping — the
    // anchor guard in splitPrinted needs the title intact to tell Model-A from legacy.
    var split = splitPrinted(n, productType);
    var flavor = split[0];
    n = split[1];
    if (set) n = n.replace('[' + set + ']', '').replace('- ' + set, '');
    else n = n.replace(/\s*\[[^\]]*\]\s*$/, '');
    var hinted = PRINTING_HINT.test(n);
    n = n.replace(PRINTING_HINT, '');
    var treat = '';
    var m = n.match(TRAIL_PAREN);
    if (m) {
      var group = String(m[1]).trim();
      // Whatever survives the anchor strip is the TREATMENT now, hinted or not. The old code
      // had to guess — a trailing group meant "vanity name" on a Model-A title and "treatment"
      // on a legacy one — because the vanity name used to live in a second paren group. Moving
      // it to a prefix removed the ambiguity outright, so `hinted` no longer selects a meaning;
      // it is kept only to document that both shapes now resolve the same way.
      treat = group;
      n = n.slice(0, m.index);
    }
    n = n.replace(/\b(reverse holofoil|cold foil|holofoil|foil)\b\s*$/i, '');
    // A bare "(29/94)" is a collector number, not a treatment — don't surface it.
    if (/^[0-9/]+$/.test(treat)) treat = '';
    if (/^[0-9/]+$/.test(flavor)) flavor = '';
    return {
      name: n.replace(/\s+/g, ' ').replace(/[-–]\s*$/, '').trim(),
      treatment: treat,
      flavorName: flavor
    };
  }

  // Shopify productType → the game token the search index and /api/search use
  // ("MTG", "Pokemon" — see search-doc.ts). Returns null for anything that isn't a
  // singles product (sealed, board games), so callers can tell "not a card" apart
  // from "a card whose game we guessed".
  //
  // productType is the ONLY reliable source: Model A SKUs are prefixed with the game
  // (POKEMON-FO-0162-HF-MP) but the old catalog's are not — prod Star Wars singles
  // are SOR-423-EN-NF-2, identical in shape to Magic's.
  var GAME_BY_TYPE = {
    'mtg single': 'MTG',
    'pokemon single': 'Pokemon',
    'riftbound singles': 'Riftbound',
    'star wars: unlimited single': 'SWU',
  };
  function cardGame(productType) {
    var t = String(productType == null ? '' : productType).trim().toLowerCase();
    if (GAME_BY_TYPE[t]) return GAME_BY_TYPE[t];
    // Unknown singles line (a new game): fall back to the name before "Single(s)"
    // rather than silently claiming Magic.
    var m = t.match(/^(.*?)\s+singles?$/);
    return m && m[1] ? m[1].replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : null;
  }

  // ── Missing-card-art placeholder ──────────────────────────────────────────
  // A card with no art must never render as empty space: the row collapses, the
  // text slides left, and the page reads as broken rather than as "we don't have
  // a photo of this one". Every surface that draws card art goes through here.
  //
  // WE DO NOT INVENT ART. The placeholder is the GAME'S CARD BACK — honest about
  // being a stand-in, and card-shaped so the layout is identical to a real card.
  // Substituting some other printing's art is how a customer ends up looking at a
  // card they aren't buying (see CATALOG-BUILD-SPEC §5.1/§5.2).
  //
  // Card-back files are self-hosted (assets/cardback-<game>.<ext>) and their URLs
  // are handed in via window.TTT_ASSETS.cardbacks by layout/theme.liquid, because
  // this file is a plain browser-cached asset with no Liquid in it. A game with no
  // card back on file degrades to a titled card-shaped tile rather than a gap —
  // dropping the file in and adding one line to the Liquid map upgrades it.
  // Callers hand us either a Shopify product type ("Riftbound Singles") or the bare
  // game token the search index carries ("RIFTBOUND"). Both are normalised here so a
  // call site can't miss the back by passing the shape the other surface uses.
  var CARDBACK_KEY = {
    'mtg': 'mtg', 'magic': 'mtg', 'magic: the gathering': 'mtg',
    'pokemon': 'pokemon', 'pokémon': 'pokemon',
    'riftbound': 'riftbound',
    'swu': 'swu', 'star wars': 'swu', 'star wars: unlimited': 'swu',
    'lorcana': 'lorcana', 'disney lorcana': 'lorcana',
    'flesh and blood': 'fab', 'fab': 'fab',
  };

  /** URL of the card back for a product type OR bare game, or null when we host none. */
  function cardBack(gameOrType) {
    var backs = (window.TTT_ASSETS && window.TTT_ASSETS.cardbacks) || {};
    var raw = String(gameOrType == null ? '' : gameOrType).trim().toLowerCase();
    var stripped = raw.replace(/\s+singles?$/, '').replace(/\s+sealed$/, '');
    var key = CARDBACK_KEY[stripped] || CARDBACK_KEY[raw]
      || CARDBACK_KEY[String(cardGame(gameOrType) || '').toLowerCase()];
    return (key && backs[key]) || null;
  }

  /**
   * A DOM element to draw where card art would go. Always card-shaped.
   * `opts.name` / `opts.sub` label the fallback tile when we have no card back.
   */
  function cardPlaceholder(productType, opts) {
    var o = opts || {};
    var url = cardBack(productType);
    var box = document.createElement('div');
    box.className = 'ttt-cardph' + (o.className ? ' ' + o.className : '');
    if (url) {
      var img = document.createElement('img');
      img.src = url;
      img.loading = 'lazy';
      // Decorative: the real card name is already in the row's text, and announcing
      // "card back" on every missing card just adds noise for a screen reader.
      img.alt = '';
      // A 404 on the back must not reintroduce the blank this exists to prevent.
      img.onerror = function () { img.remove(); box.classList.add('ttt-cardph-txt'); fill(); };
      box.appendChild(img);
      box.title = (o.name ? o.name + ' — ' : '') + 'no image on file yet';
      return box;
    }
    box.classList.add('ttt-cardph-txt');
    fill();
    return box;

    function fill() {
      var n = document.createElement('span');
      n.className = 'ttt-cardph-nm';
      n.textContent = o.name || 'No image';
      box.appendChild(n);
      if (o.sub) {
        var s = document.createElement('span');
        s.className = 'ttt-cardph-sub';
        s.textContent = o.sub;
        box.appendChild(s);
      }
    }
  }

  // ── Cart quantity ledger ──────────────────────────────────────────────────
  // Every prod variant is tracked with inventoryPolicy DENY, so Shopify already
  // refuses to oversell — but it does that with a 422 *after* the click, and it
  // counts the whole cart, not just the add in front of it. These helpers let the
  // UI show the real ceiling up front: what's on the shelf MINUS what the shopper
  // is already holding, so two passes over a picker can't ask for 8 copies of 4.
  //
  // Fails open on purpose: if /cart.js can't be read we treat the cart as empty
  // rather than blocking an add the server would have accepted.
  var cartQty = null;   // variantId (string) → quantity in cart; null = never loaded
  var cartLoad = null;  // cached/in-flight load promise

  function indexCart(cart) {
    var m = {};
    ((cart && cart.items) || []).forEach(function (it) {
      var id = String(it.variant_id != null ? it.variant_id : it.id);
      m[id] = (m[id] || 0) + (it.quantity || 0);
    });
    cartQty = m;
    return m;
  }

  function loadCart(force) {
    // A sync() from somewhere else (the search page already reads /cart.js for its
    // badge) counts as loaded — don't refetch behind it.
    if (cartQty && !force) return Promise.resolve(cartQty);
    if (cartLoad && !force) return cartLoad;
    cartLoad = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { return c ? indexCart(c) : (cartQty || {}); })
      .catch(function () { return cartQty || {}; });
    return cartLoad;
  }

  // How many of `variantId` are already in the cart. 0 until the cart has loaded —
  // callers that build UI synchronously should re-clamp on load()'s promise.
  function inCart(variantId) {
    return (cartQty && cartQty[String(variantId)]) || 0;
  }

  // How many MORE of `variantId` may be added. A null/undefined `stock` means the
  // variant is untracked or set to keep selling — no ceiling, not zero.
  function remaining(variantId, stock) {
    if (stock == null || stock === Infinity) return Infinity;
    return Math.max(0, (parseInt(stock, 10) || 0) - inCart(variantId));
  }

  // Shopper-readable text out of a /cart/*.js error body. Shopify puts the useful
  // sentence ("You can only add 3 of X to the cart") in `description`.
  function cartError(body, fallback) {
    var d = body && (body.description || body.message);
    return d ? String(d) : (fallback || 'Could not add to cart.');
  }

  window.TTT = window.TTT || {};
  window.TTT.cart = {
    load: loadCart,
    refresh: function () { return loadCart(true); },
    sync: indexCart,
    inCart: inCart,
    remaining: remaining,
    error: cartError,
  };

  // Everything in the theme that mutates the cart fires this. A full cart object in
  // `detail` re-indexes for free; anything else costs one refetch.
  window.addEventListener('ttt:cart-updated', function (e) {
    if (e && e.detail && e.detail.items) indexCart(e.detail);
    else loadCart(true);
  });

  // ---- Sets we're not buying (buylist set config) ----------------------------
  // A new set is routinely closed to buying for a while after release (prices are
  // wild, fakes circulate). The backend refuses those lines authoritatively; this
  // is the copy the UI uses to grey a printing out BEFORE the shopper picks it,
  // instead of letting them add a card and then explaining the refusal.
  //
  //   TTT.buylist.load(apiBase)      → Promise, fetched once per page
  //   TTT.buylist.blocked(game,code) → { note } when closed, else null
  //   TTT.buylist.buysOnline(game)   → does the sell page take this game at all
  //
  // Which GAMES the sell page takes comes from the same endpoint (Admin → Buylist →
  // Config → Pricing → "Buy online"). Until it loads, or when the backend predates the
  // field, it's Magic + Pokémon — the list this file used to hard-code — so a new game
  // never opens by accident. The quote endpoint refuses offline games either way.
  var ONLINE_DEFAULT = ['MTG', 'POKEMON'];
  var GAME_NAMES = { MTG: 'Magic: The Gathering', POKEMON: 'Pokémon', RIFTBOUND: 'Riftbound', SWU: 'Star Wars: Unlimited' };
  var blocked = null;      // null until loaded; {} means "nothing is blocked"
  var onlineGames = null;
  var blockedPromise = null;

  function loadBuylistBlocks(apiBase) {
    if (blockedPromise) return blockedPromise;
    var base = String(apiBase || '').replace(/\/$/, '');
    blockedPromise = fetch(base + '/api/buylist/config')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        blocked = (d && d.blockedSets) || {};
        onlineGames = (d && Array.isArray(d.onlineGames)) ? d.onlineGames : ONLINE_DEFAULT;
        return blocked;
      })
      // Fail OPEN on sets: if this can't load, nothing greys out and the quote endpoint
      // still refuses blocked sets server-side. Worse UX, never a wrong purchase.
      .catch(function () { blocked = {}; onlineGames = ONLINE_DEFAULT; return blocked; });
    return blockedPromise;
  }

  function buylistBlocked(game, code) {
    if (!blocked || !code) return null;
    var g = blocked[String(game || 'MTG').toUpperCase()];
    if (!g) return null;
    var note = g[String(code).toUpperCase()];
    return note === undefined ? null : { note: note || '' };
  }

  window.TTT.buylist = {
    load: loadBuylistBlocks,
    loaded: function () { return blocked !== null; },
    /** The in-flight load, or null if nobody has started one (no API base to fetch from). */
    ready: function () { return blockedPromise; },
    blocked: buylistBlocked,
    buysOnline: function (game) {
      return (onlineGames || ONLINE_DEFAULT).indexOf(String(game || '').toUpperCase()) !== -1;
    },
    /** "Magic: The Gathering, Pokémon and Riftbound" — for the "we buy X online" message. */
    onlineGameNames: function () {
      var n = (onlineGames || ONLINE_DEFAULT).map(function (g) { return GAME_NAMES[g] || g; });
      return n.length > 1 ? n.slice(0, -1).join(', ') + ' and ' + n[n.length - 1] : (n[0] || '');
    },
    /** Customer-facing reason, note-first with a generic fallback. */
    note: function (game, code) {
      var b = buylistBlocked(game, code);
      return b ? (b.note || "We're not buying cards from this set right now.") : '';
    },
  };

  window.TTT.esc = esc;
  window.TTT.money = money;
  window.TTT.toast = toast;
  window.TTT.cardTitle = cardTitle;
  window.TTT.oracleTitle = oracleTitle;
  window.TTT.printedNameFromTitle = printedNameFromTitle;
  window.TTT.cardGame = cardGame;
  window.TTT.cardBack = cardBack;
  window.TTT.cardPlaceholder = cardPlaceholder;

  // Back-compat globals for the existing call sites (esc / money / fmt).
  if (typeof window.esc === 'undefined') window.esc = esc;
  if (typeof window.money === 'undefined') window.money = money;
  if (typeof window.fmt === 'undefined') window.fmt = money;
  if (typeof window.toast === 'undefined') window.toast = toast;
  if (typeof window.cardTitle === 'undefined') window.cardTitle = cardTitle;
})();
